import type { Router, RouteLocationNormalizedLoaded } from 'vue-router';
import { PATH_AUTH_HANDOFF } from '@/routes/root/auth.handoff.route';

const HANDOFF_ID_KEYS = ['handoff', 'handoffId'] as const;
const HANDOFF_QUERY_KEYS = new Set<string>([...HANDOFF_ID_KEYS, 'state']);

type HandoffParams = {
  handoffId: string | null;
  state: string | null;
};

function getFirstQueryValue(value: unknown): string | null {
  if (Array.isArray(value)) {
    return value.length ? getFirstQueryValue(value[0]) : null;
  }
  return typeof value === 'string' ? value : null;
}

function extractHandoffParams(route: RouteLocationNormalizedLoaded): HandoffParams {
  const handoffId =
    HANDOFF_ID_KEYS.map((key) => getFirstQueryValue(route.query[key]))
      .find((value) => Boolean(value)) || null;

  const state = getFirstQueryValue(route.query.state);

  return {
    handoffId,
    state,
  };
}

function buildTarget(route: RouteLocationNormalizedLoaded): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(route.query)) {
    if (HANDOFF_QUERY_KEYS.has(key)) {
      continue;
    }

    if (value === null || value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== null && entry !== undefined) {
          params.append(key, String(entry));
        }
      });
    } else {
      params.append(key, String(value));
    }
  }

  const queryString = params.toString();
  const hash = typeof route.hash === 'string' ? route.hash : '';

  if (!queryString && !hash) {
    return route.path;
  }

  return `${route.path}${queryString ? `?${queryString}` : ''}${hash}`;
}

export default function registerCrossAppHandoffInterceptor(router: Router): void {
  router.beforeEach((to, _from, next) => {
    const { handoffId, state } = extractHandoffParams(to);

    if (
      handoffId &&
      state &&
      to.path !== PATH_AUTH_HANDOFF
    ) {
      const target = buildTarget(to);

      next({
        path: PATH_AUTH_HANDOFF,
        query: {
          handoffId,
          state,
          ...(target ? { target } : {}),
        },
        replace: true,
      });
      return;
    }

    next();
  });
}

